import {
  Card,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Progress,
  Button,
  Divider,
  Box,
  Skeleton,
} from "@mantine/core";
import {
  IconShieldCheck,
  IconClock,
  IconCalendar,
  IconBuildingBank,
  IconAlertCircle,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";

import { formatDateNumeric } from "../utils/formatters";
interface AuditOverviewCardProps {
  data: {
    pendingRequests: Array<{
      id: string;
      auditor_organization: string;
      audit_type: string;
      requested_start_date: string;
      requested_end_date: string;
      created_at: string;
    }>;
    activeAudits: Array<{
      grant_id: string;
      auditor_name: string;
      /** Null when the grant carries no organization (F-091). */
      auditor_organization: string | null;
      audit_type: string;
      valid_from: string;
      valid_until: string;
      days_remaining: number;
      progress_percentage: number;
    }>;
    hasPendingRequests: boolean;
    hasActiveAudits: boolean;
  };
  isLoading?: boolean;
}

/**
 * Dashboard card showing audit overview including pending requests and active audits
 *
 * Genuinely shared (frontend split epic Phase 7, Task 7.1 real-caller
 * check): apps/portal's own dashboard widget registration
 * (features/audit-management/dashboard/AuditOverviewWidget.tsx) renders
 * this directly — not auditor-exclusive despite the name.
 */
export function AuditOverviewCard({ data, isLoading }: AuditOverviewCardProps) {
  if (isLoading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Skeleton height={24} width="60%" />
          <Divider />
          <Skeleton height={100} />
          <Skeleton height={100} />
        </Stack>
      </Card>
    );
  }

  const { pendingRequests, activeAudits, hasPendingRequests, hasActiveAudits } =
    data;

  // Show card only if there are pending requests or active audits
  if (!hasPendingRequests && !hasActiveAudits) {
    return null;
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <IconShieldCheck size={20} />
            <Title order={4}>Audit Activity</Title>
          </Group>
          <Button
            component={Link}
            to="/settings/organization"
            variant="subtle"
            size="xs"
          >
            Manage Audits
          </Button>
        </Group>

        <Divider />

        {/* Pending Requests Section */}
        {hasPendingRequests && (
          <Box>
            <Group gap="xs" mb="sm">
              <IconAlertCircle
                size={16}
                color="var(--mantine-color-yellow-6)"
              />
              <Text size="sm" fw={600}>
                Pending Requests ({pendingRequests.length})
              </Text>
            </Group>
            <Stack gap="xs">
              {pendingRequests.slice(0, 2).map((request) => (
                <Box
                  key={request.id}
                  p="sm"
                  style={{
                    borderLeft: "3px solid var(--mantine-color-yellow-6)",
                    backgroundColor: "var(--mantine-color-default)",
                  }}
                >
                  <Group justify="space-between" mb={4}>
                    <Text size="sm" fw={500}>
                      {request.auditor_organization}
                    </Text>
                    <Badge size="sm" color="yellow">
                      Pending
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Type: {request.audit_type.replace(/_/g, " ")}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Requested: {formatDateNumeric(request.created_at)}
                  </Text>
                </Box>
              ))}
              {pendingRequests.length > 2 && (
                <Text size="xs" c="dimmed" ta="center">
                  +{pendingRequests.length - 2} more pending request
                  {pendingRequests.length - 2 !== 1 ? "s" : ""}
                </Text>
              )}
            </Stack>
          </Box>
        )}

        {/* Active Audits Section */}
        {hasActiveAudits && (
          <Box>
            <Group gap="xs" mb="sm">
              <IconClock size={16} color="var(--mantine-color-blue-6)" />
              <Text size="sm" fw={600}>
                Active Audits ({activeAudits.length})
              </Text>
            </Group>
            <Stack gap="xs">
              {activeAudits.slice(0, 2).map((audit) => (
                <Box
                  key={audit.grant_id}
                  p="sm"
                  style={{
                    borderLeft: "3px solid var(--mantine-color-blue-6)",
                    backgroundColor: "var(--mantine-color-default)",
                  }}
                >
                  <Group justify="space-between" mb={4}>
                    <Text size="sm" fw={500}>
                      {audit.auditor_organization ?? "Organization not given"}
                    </Text>
                    <Badge
                      size="sm"
                      color={audit.days_remaining < 3 ? "orange" : "blue"}
                    >
                      {audit.days_remaining} day
                      {audit.days_remaining !== 1 ? "s" : ""} left
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed" mb="xs">
                    Auditor: {audit.auditor_name}
                  </Text>
                  <Group gap="xs" mb="xs">
                    <IconCalendar size={12} />
                    <Text size="xs" c="dimmed">
                      {formatDateNumeric(audit.valid_from)} -{" "}
                      {formatDateNumeric(audit.valid_until)}
                    </Text>
                  </Group>
                  <Box>
                    <Group justify="space-between" mb={4}>
                      <Text size="xs" c="dimmed">
                        Progress
                      </Text>
                      <Text size="xs" fw={500}>
                        {audit.progress_percentage}%
                      </Text>
                    </Group>
                    <Progress
                      value={audit.progress_percentage}
                      size="sm"
                      color={audit.days_remaining < 3 ? "orange" : "blue"}
                    />
                  </Box>
                </Box>
              ))}
              {activeAudits.length > 2 && (
                <Text size="xs" c="dimmed" ta="center">
                  +{activeAudits.length - 2} more active audit
                  {activeAudits.length - 2 !== 1 ? "s" : ""}
                </Text>
              )}
            </Stack>
          </Box>
        )}

        {/* Summary Footer */}
        <Group justify="space-between" pt="xs">
          <Group gap="xs">
            <IconBuildingBank size={16} color="var(--mantine-color-gray-6)" />
            <Text size="xs" c="dimmed">
              Facility Audit Management
            </Text>
          </Group>
          <Button
            component={Link}
            to="/settings/organization"
            size="xs"
            variant="light"
          >
            View All
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
